import React, { useState } from 'react';
import { IOrder, IOrderStatus } from '@/interfaces/order';
import { Button, Checkbox, Divider, GetProp, notification } from 'antd';
import { printTemplates } from '@/configs/templates';
import Card from '@/components/card';
import Title from '@/components/title';
import { useMutation } from '@tanstack/react-query';
import { updateOrderStatus } from '@/adapters/order';
import { EOrderStatus } from '@/enums/EOrder';
import ConfirmApprove from '@/components/modal/confirm-approve';
import { IDataModal } from '@/interfaces/dataModal';
import { DetailItem } from '@/components/dataItem/data-view-item';
import { Dayjs } from 'dayjs';
import { formatDate, formatNumber } from '@/utils/dataUtils';
// import { IStockReserved } from '@/interfaces/stockReserved';
// import { addStockReserveds, deleteStockReserved } from '@/adapters/stock';

interface IProps {
  disabled: boolean;
  data: IOrder;
  onRefresh: () => void;
}

const _modal: IDataModal<IOrderStatus> = {
  open: false,
};

const OrderOutStock = ({ data, onRefresh, disabled }: IProps) => {
  const [completedOrder, setCompletedOrder] =
    useState<IDataModal<IOrderStatus>>(_modal);
  const [templates, setTemplates] = useState<string[]>(
    (data?.templates?.split(',') || data?.customer?.templates?.split(',')) ??
      [],
  );

  const onChange: GetProp<
    typeof Checkbox.Group,
    'onChange'
  > = checkedValues => {
    if (checkedValues.includes('TM16') && !checkedValues.includes('TM12')) {
      checkedValues = [...checkedValues, 'TM12'];
    }
    setTemplates(checkedValues as string[]);
  };

  const mutations = useMutation({
    mutationFn: async (data: IOrderStatus) => await updateOrderStatus(data),
    mutationKey: ['update-order-status-pioneer'],
    onSuccess: data => {
      if (data.status === 200) {
        notification.success({
          message: 'อัพเดทสถานะออเดอร์',
        });
        setCompletedOrder({ ..._modal });
        onRefresh();
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const onSentToPioneer = () => {
    mutations.mutateAsync({
      orderId: Number(data.id),
      status: completedOrder?.item?.status as EOrderStatus,
      templates: templates.toString() || '',
    });
  };

  // const createReservedMutation = useMutation({
  //   mutationFn: async (data: Partial<IStockReserved>[]) =>
  //     await addStockReserveds(data),
  //   mutationKey: ['add-stock-reserved'],
  //   onSuccess: data => {
  //     if (data.status === 200) {
  //       notification.success({
  //         message: 'จองสินค้าสำเร็จ',
  //       });
  //       setCompletedOrder({ ..._modal });
  //       onRefresh();
  //     }
  //   },
  //   onError: error => {
  //     console.log('error', error);
  //   },
  // });

  // const onCreateReserved = () => {
  //   const req: Partial<IStockReserved>[] =
  //     data.orderItems?.map(i => {
  //       const model: Partial<IStockReserved> = {
  //         isActive: true,
  //         stockId: i.stockId,
  //         orderId: data.id,
  //         quantity: i.quantity,
  //       };
  //       return model;
  //     }) || [];
  //   createReservedMutation.mutateAsync(req);
  // };

  // const onDeleteReserved = async () => {
  //   await deleteStockReserved(Number(data.id));
  //   notification.success({
  //     message: 'ยกเลิกการจองสำเร็จ',
  //   });
  //   onRefresh();
  // };

  return (
    <>
      <Title
        customClassName="text-lg text-blue-700 mt-4"
        customContainerClassName="py-0"
        title={`รายการเอกสารสำหรับส่งสินค้า`}
      >
        <div className="flex items-end gap-2">
          {/* {!data.isApprove && (
            <Button
              type="primary"
              className={data.isReserved ? 'bg-red-600' : 'bg-purple-600'}
              disabled={data.isApprove}
              loading={createReservedMutation.isPending}
              onClick={() =>
                data.isReserved ? onDeleteReserved() : onCreateReserved()
              }
            >
              {data.isReserved ? `ยกเลิกการจองสินค้า` : `จองสินค้า`}
            </Button>
          )} */}
          {data.status !== EOrderStatus.CANCEL && (
            <Button
              type="primary"
              className="bg-orange-700"
              disabled={data.isApprove}
              loading={mutations.isPending}
              onClick={() =>
                setCompletedOrder({
                  open: true,
                  id: data.id,
                  item: { status: EOrderStatus.APPROVED } as IOrderStatus,
                })
              }
            >
              {`ยืนยันการจัดสินค้า`}
            </Button>
          )}
          {data.status !== EOrderStatus.CANCEL && (
            <Button
              type="primary"
              className="bg-orange-700"
              disabled={!templates.length || !data.isApprove || data.isConfirm}
              loading={mutations.isPending}
              onClick={() =>
                setCompletedOrder({
                  open: true,
                  id: data.id,
                  item: { status: EOrderStatus.CONFIRM } as IOrderStatus,
                })
              }
            >
              {`ยืนยันการส่งเอกสาร  ${templates.length} รายการ`}
            </Button>
          )}
        </div>
      </Title>
      <Divider className="m-0" />
      <Checkbox.Group
        style={{ width: '100%' }}
        onChange={onChange}
        value={templates}
        disabled={disabled}
      >
        <div className="grid md:grid-cols-4 w-full gap-4 h-full">
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบส่งของ"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM01', 'TM02', 'TM03'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบแจ้งหนี้"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM04', 'TM05', 'TM05-2'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบกำกับภาษี"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM06', 'TM07', 'TM07-2'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบกำกับสินค้า"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM08', 'TM09'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบเสร็จรับเงิน"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM10', 'TM11', 'TM11-2'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="ใบส่งสินค้า"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t =>
                ['TM12', 'TM13', 'TM14', 'TM15', 'TM16'].includes(t.key),
              )
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
          <Card
            customClass="drop-shadow-none w-full p-3 flex flex-col h-full"
            customContainerClass="p-0 flex-grow"
          >
            <Title
              title="อื่นๆ"
              customContainerClassName="p-0"
              customClassName="text-base mb-2"
            />
            {printTemplates
              ?.filter(t => ['TM17', 'TM18', 'TM19', 'TM31'].includes(t.key))
              .map(p => (
                <Checkbox className="flex" key={p.key} value={p.key}>
                  {p.name}
                </Checkbox>
              ))}
          </Card>
        </div>
      </Checkbox.Group>
      <ConfirmApprove
        content={
          (completedOrder?.item?.status as EOrderStatus) ===
          EOrderStatus.APPROVED
            ? `ยืนยันการจัดสินค้าใช่หรือไม่`
            : `ยืนยันการส่งเอกสารใช่หรือไม่`
        }
        handleCancel={() => {
          setCompletedOrder({ ..._modal });
        }}
        handleOk={() => {
          onSentToPioneer();
        }}
        open={completedOrder?.open}
        title={`ออเดอร์ '${data?.code}'`}
        loading={mutations?.isPending}
        showInfo
        info={
          <div className="flex flex-col justify-end mt-2 gap-2">
            <DetailItem
              customLabelClass="md:min-w-[80px]"
              label="ลูกค้า"
              detail={data?.customerName}
            />
            <DetailItem
              customLabelClass="md:min-w-[80px]"
              label="วันที่สั่งซื้อ"
              detail={formatDate(data?.orderDate as Dayjs)}
            />
            <DetailItem
              customLabelClass="md:min-w-[80px]"
              label="CO"
              detail={data?.soNo}
            />
            <DetailItem
              customLabelClass="md:min-w-[80px]"
              label="IV"
              detail={data?.ivNo}
            />
            <DetailItem
              customLabelClass="md:min-w-[80px]"
              label="PO"
              detail={data?.poNo}
            />
            <DetailItem
              customLabelClass="md:min-w-[80px]"
              label="ออเดอร์"
              detail={`${data?.orderItems?.length} รายการ`}
            />
            <div className="flex flex-col border border-red-400 rounded-lg p-2 text-bold w-full bg-red-50">
              <DetailItem
                customMainClass="flex-row"
                customLabelClass="flex justify-end min-w-[140px]  text-sm"
                label="รวมเงิน"
                detail={formatNumber(Number(data?.totalAmount), 2)}
                customClass="flex justify-end w-full text-black font-bold  text-sm"
              />
              <DetailItem
                customMainClass="flex-row"
                customLabelClass="flex justify-end min-w-[140px]    text-sm"
                label="ส่วนลด"
                detail={formatNumber(Number(data?.discountAmount), 2)}
                customClass="flex justify-end w-full text-black font-bold  text-sm"
              />
              <DetailItem
                customMainClass="flex-row"
                customLabelClass="flex justify-end min-w-[140px]    text-sm"
                label="รวมเงินหลังหักส่วนลด"
                detail={formatNumber(
                  Number(
                    (data?.totalAmount || 0) -
                      Number(data?.discountAmount || 0),
                  ),
                  2,
                )}
                customClass="flex justify-end w-full text-black font-bold  text-sm"
              />
              <DetailItem
                customMainClass="flex-row"
                customLabelClass="flex justify-end min-w-[140px]    text-sm"
                label={`${data?.vatRate} %`}
                detail={formatNumber(data?.vatAmount, 2)}
                customClass="flex justify-end w-full text-black font-bold  text-sm"
              />
              <DetailItem
                customMainClass="flex-row"
                customLabelClass="flex justify-end min-w-[140px]   text-sm"
                label="ยอดสุทธิ"
                detail={formatNumber(data?.summary, 2)}
                customClass="flex justify-end w-full text-black font-bold  text-sm"
              />
            </div>
          </div>
        }
      />
    </>
  );
};

export default OrderOutStock;
